import React, { useEffect, useState } from 'react';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import companyService from '../../services/CompanyService.js';
import {
  isCompanyExportDownloaded,
  markCompanyExportDownloaded,
} from '../../utils/companyExportGate.js';
import { X, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DeleteCompanyModal = ({ company, onClose, onSuccess }) => {
  const [loadingChallenge, setLoadingChallenge] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [challengeToken, setChallengeToken] = useState('');
  const [displayCode, setDisplayCode] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportDownloaded, setExportDownloaded] = useState(() =>
    isCompanyExportDownloaded(company.id)
  );

  useEffect(() => {
    const loadChallenge = async () => {
      try {
        setLoadingChallenge(true);
        const response = await companyService.requestDeleteChallenge(company.id);
        setConfirmationCode(response.confirmation_code || '');
        setChallengeToken(response.challenge_token || '');
        setDisplayCode(response.confirmation_code || '');
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to start delete confirmation');
        onClose();
      } finally {
        setLoadingChallenge(false);
      }
    };
    loadChallenge();
  }, [company.id, onClose]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const { filename, cancelled } = await companyService.exportExcel(company.id);
      if (cancelled) return;
      markCompanyExportDownloaded(company.id);
      setExportDownloaded(true);
      Alert.success(`Downloaded ${filename}`);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const payload = JSON.parse(text);
          Alert.error(payload.error || 'Failed to export company data');
        } catch {
          Alert.error('Failed to export company data');
        }
      } else {
        Alert.error(err.response?.data?.error || 'Failed to export company data');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!enteredCode.trim() || !deletePassword.trim()) {
      Alert.error('Enter the confirmation code and delete password');
      return;
    }
    try {
      setSubmitting(true);
      await companyService.purgeCompany(company.id, {
        confirmation_code: enteredCode.trim(),
        challenge_token: challengeToken,
        delete_password: deletePassword,
      });
      Alert.success('Company deleted permanently');
      onSuccess();
      onClose();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to delete company');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Delete Company Permanently</h2>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-gray-700">
            You are about to permanently delete <strong>{company.company_name}</strong> and{' '}
            <strong>all related data</strong> (users, shipments, subscriptions, vehicles, settings, and every other record for this company).
            This cannot be undone.
          </p>

          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={handleExport}
            disabled={exporting || submitting}
          >
            {exporting ? 'Preparing download...' : exportDownloaded ? 'Re-download Data (Excel)' : 'Download Data (Excel)'}
          </Button>

          {loadingChallenge ? (
            <p className="text-sm text-gray-500">Generating confirmation code...</p>
          ) : (
            <>
              <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-red-700 font-semibold mb-1">
                  Confirmation Code
                </p>
                <p className="text-3xl font-bold tracking-[0.3em] text-red-700">{displayCode}</p>
                <p className="text-xs text-red-600 mt-2">
                  Code changes each time this dialog is opened. Enter the code shown above with the delete password.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter Confirmation Code
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={enteredCode}
                  onChange={(e) => setEnteredCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  placeholder="6-digit code"
                  autoComplete="off"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Delete Password
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  placeholder="10-character delete password"
                  autoComplete="off"
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              className="flex-1"
              disabled={loadingChallenge || submitting || !confirmationCode || !challengeToken}
            >
              {submitting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeleteCompanyModal;
